import React, { useRef, useState } from "react";
import {
  Button,
  CircularProgress,
  Popover,
  Typography,
} from "@mui/material";
import axios from "../../utils/axios-middleware";
import { showError } from "../../toastify";

const POLL_INTERVAL_MS = 600;
const POLL_MAX_ATTEMPTS = 9; // ~5.4 s total

async function waitForLaunchConfirmation(token) {
  for (let i = 0; i < POLL_MAX_ATTEMPTS; i++) {
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    try {
      const { data } = await axios.get(
        `/api/launch-status?token=${encodeURIComponent(token)}`
      );
      if (data.confirmed) return true;
    } catch {
      // network hiccup — keep polling
    }
  }
  return false;
}

export default function OpenFromDesktopButton({ applicationId, positionName }) {
  const [status, setStatus] = useState("idle"); // idle | fetching | opening | popover
  const [releaseInfo, setReleaseInfo] = useState(null);
  const anchorRef = useRef(null);

  async function handleClick() {
    setStatus("fetching");
    try {
      const { data } = await axios.post("/api/examination/launch-token", {
        applicationId,
      });

      window.location.href = `mifotra-exam://launch?token=${data.token}`;
      setStatus("opening");

      const confirmed = await waitForLaunchConfirmation(data.token);

      if (confirmed) {
        setStatus("idle");
        return;
      }

      // App not detected — fetch release info and show download popover
      try {
        const { data: info } = await axios.get("/api/release-info?platform=win");
        setReleaseInfo(info);
      } catch {
        setReleaseInfo(null);
      }
      setStatus("popover");
    } catch (err) {
      setStatus("idle");
      const msg = err?.response?.data || "Failed to generate exam launch token.";
      showError(typeof msg === "string" ? msg : "Failed to launch exam.");
    }
  }

  const busy = status === "fetching" || status === "opening";

  return (
    <>
      <Button
        ref={anchorRef}
        disableElevation
        size="small"
        variant="contained"
        color="primary"
        onClick={handleClick}
        disabled={busy}
        startIcon={
          busy ? <CircularProgress size={14} color="inherit" /> : null
        }
        aria-label={`Open ${positionName} exam from desktop app`}
      >
        <span aria-hidden="true">
          {status === "fetching"
            ? "Preparing…"
            : status === "opening"
              ? "Opening…"
              : "Open from Desktop"}
        </span>
      </Button>

      <Popover
        open={status === "popover"}
        anchorEl={anchorRef.current}
        onClose={() => setStatus("idle")}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { p: 2, maxWidth: 280 } }}
      >
        <Typography variant="body2" gutterBottom>
          The <strong>MIFOTRA e-Examination</strong> app is not installed.
          Download and install it, then click{" "}
          <em>Open from Desktop</em> again.
        </Typography>

        {releaseInfo?.version && (
          <Typography
            variant="caption"
            color="textSecondary"
            display="block"
            sx={{ mb: 1 }}
          >
            Latest: <strong>{releaseInfo.version}</strong>
            {releaseInfo.filename && <> &mdash; {releaseInfo.filename}</>}
          </Typography>
        )}

        {releaseInfo?.downloadUrl ? (
          <a
            href={releaseInfo.downloadUrl}
            target="_blank"
            rel="noreferrer"
            style={{ textDecoration: "none" }}
          >
            <Button
              variant="contained"
              color="primary"
              size="small"
              fullWidth
              disableElevation
            >
              Download for Windows
            </Button>
          </a>
        ) : (
          <Typography variant="caption" color="error">
            Could not fetch the download link. Please contact support.
          </Typography>
        )}
      </Popover>
    </>
  );
}
